import { useState, useEffect, useRef } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE

const SECTION_TYPE_OPTIONS = [
  { value: 'story', label: '📖 故事', color: '#1890ff' },
  { value: 'craft', label: '🎨 手工', color: '#52c41a' },
  { value: 'drama', label: '🎭 戏剧游戏', color: '#722ed1' },
  { value: 'discussion', label: '💬 讨论', color: '#fa8c16' },
  { value: 'break', label: '☕ 休息', color: '#999' },
  { value: 'performance', label: '🎬 展演', color: '#eb2f96' },
]

const VIDEO_TYPE_OPTIONS = [
  { value: 'animation', label: '🎬 动画版', color: '#1890ff' },
  { value: 'paperPuppet', label: '📄 纸偶版', color: '#52c41a' },
  { value: 'handPuppet', label: '🖐 手偶版', color: '#722ed1' },
  { value: 'liveStage', label: '🎭 真人舞台', color: '#eb2f96' },
]

const typeMap = {}
SECTION_TYPE_OPTIONS.forEach(t => typeMap[t.value] = t)
VIDEO_TYPE_OPTIONS.forEach(t => typeMap[t.value] = t)
const getDisplayName = (f) => f.displayName || f.name

export default function CoursewareUpload() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [files, setFiles] = useState([])
  const [activeProdIdx, setActiveProdIdx] = useState(0)
  const [activeLessonId, setActiveLessonId] = useState(null)
  const [editTab, setEditTab] = useState('lessons') // lessons | files
  const [msg, setMsg] = useState('')
  const fileInputRef = useRef()

  useEffect(() => { loadData(); loadFiles() }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/courseware/data`)
      const d = await res.json()
      if (d.success && d.data?.productions?.length > 0) {
        setData(d.data)
        setActiveLessonId(d.data.productions[0]?.lessons?.[0]?.id || null)
      } else {
        setData({ version: '#000', productions: [], lastUpdated: null })
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const loadFiles = async () => {
    try {
      const res = await fetch(`${API}/courseware/files`)
      const d = await res.json()
      if (d.success) setFiles(d.data || [])
    } catch {}
  }

  const showMsg = (text, type = 'success') => {
    setMsg(text); setTimeout(() => setMsg(''), 3500)
  }

  const prod = data?.productions?.[activeProdIdx]
  const lesson = prod?.lessons?.find(l => l.id === activeLessonId) || prod?.lessons?.[0]

  const cloneData = () => JSON.parse(JSON.stringify(data))

  const handleSave = async () => {
    if (!data) return
    setSaving(true)
    try {
      const res = await fetch(`${API}/courseware/data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      const d = await res.json()
      if (d.success) {
        setData(prev => ({ ...prev, lastUpdated: d.lastUpdated }))
        showMsg('✅ 保存成功')
      } else showMsg('❌ 保存失败', 'error')
    } catch (e) { showMsg('❌ 保存失败', 'error') }
    setSaving(false)
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/courseware/upload`, { method: 'POST', body: fd })
      const d = await res.json()
      if (d.success) { showMsg('✅ 上传成功'); loadFiles() }
      else showMsg('❌ 上传失败', 'error')
    } catch { showMsg('❌ 上传失败', 'error') }
    e.target.value = ''
  }

  const handleDeleteFile = async (name) => {
    await fetch(`${API}/courseware/files/${encodeURIComponent(name)}`, { method: 'DELETE' })
    loadFiles(); showMsg('\u{1F5D1}\uFE0F 已删除')
  }

  const [renameFile, setRenameFile] = useState(null) // 当前正在重命名的文件名
  const [renameValue, setRenameValue] = useState('') // 新文件名
  const handleRenameStart = (name) => {
    setRenameFile(name)
    setRenameValue(name)
  }
  const handleRenameCancel = () => { setRenameFile(null); setRenameValue('') }
  const handleRenameConfirm = async () => {
    if (!renameValue || renameValue === renameFile) {
      handleRenameCancel()
      return
    }
    try {
      const res = await fetch(`${API}/courseware/files/${encodeURIComponent(renameFile)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: renameValue }),
      })
      const d = await res.json()
      if (d.success) {
        showMsg('\u2705 已重命名')
        setRenameFile(null)
        setRenameValue('')
        loadFiles()
      } else {
        showMsg('\u274C ' + (d.error || '重命名失败'), 'error')
      }
    } catch {
      showMsg('\u274C 重命名失败', 'error')
    }
    handleRenameCancel()
  }

  // --- 课程/课时编辑 ---
  const updateProdField = (field, value) => {
    const d = cloneData()
    d.productions[activeProdIdx][field] = value
    setData(d)
  }

  const updateLessonField = (field, value) => {
    if (!lesson || !prod) return
    const d = cloneData()
    const l = d.productions[activeProdIdx].lessons.find(l => l.id === activeLessonId)
    if (l) l[field] = value
    setData(d)
  }

  const updateSectionField = (periodKey, idx, field, value) => {
    if (!lesson?.sections?.[periodKey]?.[idx]) return
    const d = cloneData()
    const l = d.productions[activeProdIdx].lessons.find(l => l.id === activeLessonId)
    if (l) l.sections[periodKey][idx][field] = value
    setData(d)
  }

  const updateVideo = (periodKey, idx, videoType, url) => {
    if (!lesson?.sections?.[periodKey]?.[idx]) return
    const d = cloneData()
    const l = d.productions[activeProdIdx].lessons.find(l => l.id === activeLessonId)
    if (l) {
      const sec = l.sections[periodKey][idx]
      if (!sec.video) sec.video = {}
      sec.video[videoType] = url
      sec.video_url = url // also write flat field for child/inst view
    }
    setData(d)
  }

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => showMsg('📋 已复制到剪贴板'))
  }

  const addLesson = () => {
    if (!prod) return
    const d = cloneData()
    const p = d.productions[activeProdIdx]
    const newId = (p.lessons?.length || 0) + 1
    p.lessons.push({
      id: newId,
      theme: '',
      goal: '',
      sections: { p0: [], p1: [] },
    })
    setData(d)
    setActiveLessonId(newId)
  }

  const addSection = (periodKey) => {
    if (!lesson) return
    const d = cloneData()
    const l = d.productions[activeProdIdx].lessons.find(l => l.id === activeLessonId)
    if (l) {
      if (!l.sections) l.sections = { p0: [], p1: [] }
      if (!l.sections[periodKey]) l.sections[periodKey] = []
      l.sections[periodKey].push({ type: 'story', title: '', duration: 10, content: '', video: {} })
    }
    setData(d)
  }

  const deleteSection = (periodKey, idx) => {
    if (!lesson?.sections?.[periodKey]) return
    const d = cloneData()
    const l = d.productions[activeProdIdx].lessons.find(l => l.id === activeLessonId)
    if (l && l.sections[periodKey]) l.sections[periodKey].splice(idx, 1)
    setData(d)
  }

  // 直接把文件URL填入当前环节的视频字段（自动选择匹配的版本类型）
  const fillVideoFromFile = (periodKey, idx, fileUrl) => {
    // 根据文件扩展名判断类型
    const ext = fileUrl.split('.').pop().toLowerCase()
    let videoType = 'animation' // 默认
    if (ext === 'mp4' || ext === 'webm' || ext === 'mov') {
      // 动画/视频类
      // 根据文件名关键词匹配版本
      const nameLower = fileUrl.toLowerCase()
      if (nameLower.includes('paper') || nameLower.includes('纸偶')) videoType = 'paperPuppet'
      else if (nameLower.includes('hand') || nameLower.includes('手偶')) videoType = 'handPuppet'
      else if (nameLower.includes('live') || nameLower.includes('真人') || nameLower.includes('舞台')) videoType = 'liveStage'
      else videoType = 'animation'
    }
    updateVideo(periodKey, idx, videoType, fileUrl.replace(/https?:\/\/[^\/]+\/storage\/|^\/storage\//, "/cms/api/ai-videos/proxy/video?path="))
    showMsg('✅ 已填入视频URL')
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>加载中...</div>
  if (!data || !data.productions?.length) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}><p>暂无课件数据</p><p style={{ fontSize: 12 }}>请先在 CMS 后台添加课程</p></div>
  }

  const hasLessons = prod?.lessons?.length > 0

  const btnStyle = (active) => ({
    padding: '6px 16px', border: active ? '2px solid #1890ff' : '1px solid #d9d9d9',
    borderRadius: 4, cursor: 'pointer', background: active ? '#e6f7ff' : '#fff',
    color: active ? '#1890ff' : '#666', fontSize: 13, fontWeight: active ? 600 : 400,
  })

  const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      {/* 顶部栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, margin: 0 }}>📚 课件上传</h2>
          <p style={{ color: '#888', fontSize: 12, margin: '4px 0 0' }}>
            {data.productions.length}部课程 · {data.productions.reduce((s, p) => s + (p.lessons?.length || 0), 0)}课时
            {data.lastUpdated && <span style={{ marginLeft: 12 }}>上次保存：{new Date(data.lastUpdated).toLocaleString('zh-CN')}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setEditTab('lessons')} style={btnStyle(editTab === 'lessons')}>📖 课程编辑</button>
          <button onClick={() => setEditTab('files')} style={btnStyle(editTab === 'files')}>📁 文件管理</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '8px 20px', background: saving ? '#ccc' : '#1890ff', color: '#fff',
            border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13,
          }}>
            {saving ? '保存中...' : '💾 保存'}
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ padding: '8px 14px', borderRadius: 4, marginBottom: 14, background: msg.startsWith('❌') ? '#fff2f0' : '#f6ffed', color: msg.startsWith('❌') ? '#ff4d4f' : '#52c41a', border: `1px solid ${msg.startsWith('❌') ? '#ffccc7' : '#b7eb8f'}`, fontSize: 13 }}>
          {msg}
        </div>
      )}

      {editTab === 'files' ? (
        /* ========== 文件管理 ========== */
        <div style={{ background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, margin: 0 }}>📁 已上传文件</h3>
            <button onClick={() => fileInputRef.current?.click()} style={{ padding: '8px 18px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
              📤 上传文件
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="video/*,audio/*,image/*" onChange={handleUpload} style={{ display: 'none' }} />
          {files.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>暂无上传文件</div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {files.map(f => (
                <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                  {renameFile === f.name ? (
                    <input
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRenameConfirm(); if (e.key === 'Escape') handleRenameCancel() }}
                      style={{ flex: 1, padding: '4px 8px', fontSize: 13, border: '1px solid #1890ff', borderRadius: 4, outline: 'none' }}
                      autoFocus
                    />
                  ) : (
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getDisplayName(f)}>{getDisplayName(f)}</span>
                  )}
                  <span style={{ fontSize: 11, color: '#999', flexShrink: 0 }}>{f.sizeMB}MB</span>
                  <button onClick={() => copyUrl(f.url)} style={{ fontSize: 11, color: '#1890ff', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }} title="复制 URL">📋</button>
                  <button onClick={() => window.open(f.url.replace(/https?:\/\/[^\/]+\/storage\/|^\/storage\//, "/cms/api/ai-videos/proxy/video?path="))} style={{ fontSize: 11, color: '#52c41a', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }} title="预览">🔗</button>
                  {renameFile === f.name ? (
                    <>
                      <button onClick={handleRenameConfirm} style={{ fontSize: 11, color: '#52c41a', background: 'none', border: '1px solid #52c41a', borderRadius: 4, cursor: 'pointer', padding: '2px 8px' }}>✔</button>
                      <button onClick={handleRenameCancel} style={{ fontSize: 11, color: '#999', background: 'none', border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', padding: '2px 8px' }}>✖</button>
                    </>
                  ) : (
                    <button onClick={() => handleRenameStart(f.name)} style={{ fontSize: 11, color: '#faad14', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }} title="重命名">✏️</button>
                  )}
                  <button onClick={() => handleDeleteFile(f.name)} style={{ fontSize: 11, color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }} title="删除">🗑</button>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize: 11, color: '#ccc', marginTop: 10 }}>上传后到课程编辑中，展开环节编辑面板，在文件列表中点击「选择」直接填入视频</p>
        </div>
      ) : (
        /* ========== 课程编辑 ========== */
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 14 }}>
          {/* 左侧导航 */}
          <div style={{ background: '#fff', borderRadius: 8, padding: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>选择课程</div>
              {data.productions.map((p, i) => (
                <button key={p.id} onClick={() => { setActiveProdIdx(i); setActiveLessonId(p.lessons?.[0]?.id || null) }}
                  style={{ display: 'block', width: '100%', padding: '8px 10px', background: activeProdIdx === i ? '#e6f7ff' : 'transparent', border: activeProdIdx === i ? '1px solid #1890ff' : '1px solid #f0f0f0', borderRadius: 6, cursor: 'pointer', marginBottom: 4, fontSize: 12, color: activeProdIdx === i ? '#1890ff' : '#666', textAlign: 'left', fontWeight: activeProdIdx === i ? 600 : 400 }}>
                  {p.name}
                  <span style={{ float: 'right', fontSize: 10, color: '#999' }}>{p.lessons?.length || 0}课</span>
                </button>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: '#888' }}>课时列表</span>
                <button onClick={addLesson} style={{ fontSize: 11, color: '#1890ff', background: 'none', border: '1px solid #1890ff', borderRadius: 4, cursor: 'pointer', padding: '2px 8px' }}>+ 课时</button>
              </div>
              {hasLessons ? prod.lessons.map(l => {
                const hasVideo = l.sections && Object.values(l.sections).some(arr => arr?.some(s => s?.video && Object.values(s.video).some(v => !!v)))
                return (
                  <button key={l.id} onClick={() => setActiveLessonId(l.id)}
                    style={{ display: 'block', width: '100%', padding: '6px 8px', background: activeLessonId === l.id ? '#f0f5ff' : 'transparent', border: 'none', borderLeft: activeLessonId === l.id ? '3px solid #1890ff' : '3px solid transparent', cursor: 'pointer', marginBottom: 2, fontSize: 12, textAlign: 'left', color: activeLessonId === l.id ? '#1890ff' : '#666' }}>
                    <span style={{ fontWeight: activeLessonId === l.id ? 600 : 400 }}>第{l.id}课</span>
                    <span style={{ marginLeft: 4, fontSize: 11, color: '#aaa' }}>{l.theme || ''}</span>
                    {hasVideo && <span style={{ float: 'right', fontSize: 10 }}>📹</span>}
                  </button>
                )
              }) : <div style={{ fontSize: 11, color: '#ccc', padding: '8px 0', textAlign: 'center' }}>暂无课时</div>}
            </div>
          </div>

          {/* 右侧编辑区 */}
          {lesson && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* 课时基本信息 */}
              <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ fontSize: 15, margin: 0 }}>📖 {prod.name} — 第{lesson.id}课</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>课时主题</label>
                    <input value={lesson.theme || ''} onChange={e => updateLessonField('theme', e.target.value)} style={inputStyle} placeholder="例：小兔子找朋友" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>课时编号</label>
                    <input value={lesson.id} onChange={e => updateLessonField('id', parseInt(e.target.value) || 0)} style={{ ...inputStyle, width: 80 }} type="number" min="1" />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>学习目标</label>
                  <textarea value={lesson.goal || ''} onChange={e => updateLessonField('goal', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="用；号分隔多个目标" />
                </div>
              </div>

              {/* 环节编辑 */}
              <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <h3 style={{ fontSize: 15, margin: '0 0 12px' }}>📋 课时环节</h3>
                {['p0', 'p1'].map(pk => {
                  const sections = lesson.sections?.[pk] || []
                  const periodName = pk === 'p0' ? '第一课时' : '第二课时'
                  return (
                    <div key={pk} style={{ marginBottom: 18 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{periodName}</span>
                        <button onClick={() => addSection(pk)} style={{ fontSize: 12, color: '#1890ff', background: 'none', border: '1px solid #1890ff', borderRadius: 4, cursor: 'pointer', padding: '3px 10px' }}>+ 环节</button>
                      </div>
                      {sections.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#ccc', padding: 10, textAlign: 'center', background: '#fafafa', borderRadius: 4 }}>暂无环节，点击上方添加</div>
                      ) : sections.map((sec, si) => (
                        <SectionEditor
                          key={si}
                          section={sec}
                          periodKey={pk}
                          idx={si}
                          files={files}
                          onChange={(field, val) => updateSectionField(pk, si, field, val)}
                          onVideoChange={(vt, url) => updateVideo(pk, si, vt, url)}
                          onDelete={() => deleteSection(pk, si)}
                          onFillVideo={(url) => fillVideoFromFile(pk, si, url)}
                        />
                      ))}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {!lesson && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <p>请选择课程和课时进行编辑</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* 环节编辑器 */
function SectionEditor({ section, periodKey, idx, files, onChange, onVideoChange, onDelete, onFillVideo }) {
  const [expanded, setExpanded] = useState(false)
  const typeInfo = typeMap[section.type] || { label: '环节', color: '#666' }

  return (
    <div style={{ marginBottom: 8, border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
      <div onClick={() => setExpanded(!expanded)} style={{
        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        background: expanded ? '#f0f5ff' : '#fff',
      }}>
        <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 6, background: typeInfo.color + '18', color: typeInfo.color, fontWeight: 500 }}>{typeInfo.label}</span>
        <input value={section.title || ''} onClick={e => e.stopPropagation()} placeholder="环节标题"
          onChange={e => { e.stopPropagation(); onChange('title', e.target.value) }}
          style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, outline: 'none', minWidth: 0 }} />
        <span style={{ fontSize: 11, color: '#999' }}>{section.duration}分钟</span>
        {section.video && Object.values(section.video).some(v => !!v) && <span style={{ fontSize: 12 }}>📹</span>}
        <span style={{ fontSize: 10, color: '#ff4d4f', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); if (window.confirm('删除此环节？')) onDelete() }}>✕</span>
        <span style={{ fontSize: 12, color: '#ccc', transform: expanded ? 'rotate(90deg)' : 'none', transition: '0.2s' }}>▶</span>
      </div>

      {expanded && (
        <div style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
          {/* 第一行：类型 + 时长 */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={section.type} onChange={e => onChange('type', e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 12 }}>
              {SECTION_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input type="number" value={section.duration} onChange={e => onChange('duration', parseInt(e.target.value) || 0)} placeholder="分钟" style={{ padding: '5px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 12, width: 70 }} />
          </div>

          {/* 内容 */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>教学指引内容</label>
            <textarea value={section.content || ''} onChange={e => onChange('content', e.target.value)} rows={3}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 12, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }} />
          </div>

          {/* 视频URL */}
          <div>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>授课视频（支持4种版本）</label>
            {VIDEO_TYPE_OPTIONS.map(vt => (
              <div key={vt.value} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: '#888', width: 70, flexShrink: 0 }}>{vt.label}</span>
                <input value={section.video?.[vt.value] || ''} onChange={e => onVideoChange(vt.value, e.target.value)}
                  placeholder="输入或粘贴视频URL" style={{ flex: 1, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 11, minWidth: 0 }} />
                {section.video?.[vt.value] && (
                  <>
                    <span style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer' }} onClick={() => navigator.clipboard.writeText(section.video[vt.value])}>📋</span>
                    <video src={section.video[vt.value]?.replace(/https?:\/\/[^\/]+\/storage\/|^\/storage\//, '/cms/api/ai-videos/proxy/video?path=')} controls controlsList="nodownload" disablePictureInPicture onContextMenu={(e)=>e.preventDefault()}
                      style={{ width: 120, height: 68, borderRadius: 4, cursor: 'pointer', objectFit: 'cover' }}
                      preload="metadata"/>
                  </>
                )}
              </div>
            ))}
            {/* 快速选择已上传文件 — 直接选择 */}
            {files.length > 0 && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer' }}>从已上传文件选择（点击「选择」直接填入上方）</summary>
                <div style={{ maxHeight: 150, overflowY: 'auto', marginTop: 4, border: '1px solid #f0f0f0', borderRadius: 4, padding: 4 }}>
                  {files.map(f => {
                    const ext = f.name.split('.').pop().toLowerCase()
                    const isVideo = ['mp4','webm','mov','avi'].includes(ext)
                    return (
                      <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', fontSize: 11, borderBottom: '1px solid #f5f5f5' }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getDisplayName(f)}>{isVideo ? '🎬' : '🖼'} {getDisplayName(f)}</span>
                        <span style={{ color: '#999', flexShrink: 0, fontSize: 10 }}>{f.sizeMB}MB</span>
                        <button onClick={() => onFillVideo(f.url)} style={{
                          fontSize: 10, color: '#fff', background: '#52c41a', border: 'none',
                          borderRadius: 3, cursor: 'pointer', padding: '2px 8px', flexShrink: 0
                        }}>选择</button>
                      </div>
                    )
                  })}
                </div>
              </details>
            )}
          </div>

          {/* 配音编辑区 */}
          <div style={{ marginTop: 10, borderTop: '1px dashed #e8e8e8', paddingTop: 10 }}>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 6 }}>🎤 配音秀设置</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={section.dubbing?.enabled || false}
                  onChange={e => {
                    const d = { ...section.dubbing }
                    d.enabled = e.target.checked
                    onChange('dubbing', d)
                  }} />
                启用配音秀
              </label>
            </div>
            {section.dubbing?.enabled && (
              <>
                {/* 配图 */}
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 10, color: '#888', display: 'block', marginBottom: 4 }}>🖼 配图（可多张，孩子端展示）</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    {(section.dubbing?.images || []).map((img, ii) => {
                      const imgUrl = typeof img === 'string' ? img : (img.url || img)
                      return (
                        <div key={ii} style={{ position: 'relative', width: 60, height: 60, borderRadius: 4, overflow: 'hidden', border: '1px solid #e8e8e8', display: 'inline-block', marginRight: 4, marginBottom: 4 }}>
                          <img src={imgUrl} alt={`配图${ii+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.style.display = 'none'; e.target.parentNode.style.display = 'none' }} />
                          <span onClick={() => {
                            const imgs = (section.dubbing?.images || []).filter((_, j) => j !== ii)
                            const d = { ...section.dubbing, images: imgs }
                            onChange('dubbing', d)
                          }} style={{
                            position: 'absolute', top: 2, right: 2, width: 16, height: 16,
                            borderRadius: 8, background: 'rgba(255,0,0,0.7)', color: '#fff',
                            fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>✕</span>
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={section.dubbing?.newImageUrl || ''} onChange={e => {
                      const d = { ...section.dubbing, newImageUrl: e.target.value }
                      onChange('dubbing', d)
                    }} placeholder="输入图片URL" style={{ flex: 1, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 11, minWidth: 0 }} />
                    <button onClick={() => {
                      const url = (section.dubbing?.newImageUrl || '').trim()
                      if (!url) return
                      const imgs = [...(section.dubbing?.images || []), url]
                      const d = { ...section.dubbing, images: imgs, newImageUrl: '' }
                      onChange('dubbing', d)
                    }} style={{
                      padding: '4px 10px', background: '#1890ff', color: '#fff', border: 'none',
                      borderRadius: 4, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap'
                    }}>添加</button>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', background: '#52c41a', color: '#fff',
                      borderRadius: 4, cursor: 'pointer', fontSize: 11, userSelect: 'none', whiteSpace: 'nowrap'
                    }}>
                      📤 上传配图
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={async e => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          const fd = new FormData()
                          fd.append('file', file)
                          try {
                            const res = await fetch(API + '/courseware/upload', { method: 'POST', body: fd })
                            const d2 = await res.json()
                            if (d2.success) {
                              showMsg('✅ 图片上传成功')
                              const imgs = [...(section.dubbing?.images || []), d2.url]
                              const nd = { ...section.dubbing, images: imgs }
                              onChange('dubbing', nd)
                              loadFiles()
                            } else {
                              showMsg('❌ ' + (d2.error || '上传失败'), 'error')
                            }
                          } catch {
                            showMsg('❌ 上传失败，网络错误', 'error')
                          }
                          e.target.value = ''
                        }} />
                    </label>
                  </div>
                  {/* 快速选择已上传图片 */}
                  {files.filter(f => f.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)).length > 0 && (
                    <details style={{ marginTop: 4 }}>
                      <summary style={{ fontSize: 10, color: '#52c41a', cursor: 'pointer' }}>从已上传图片选择</summary>
                      <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 2, border: '1px solid #f0f0f0', borderRadius: 4, padding: 4 }}>
                        {files.filter(f => f.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)).map(f => (
                          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', fontSize: 10, borderBottom: '1px solid #f5f5f5' }}>
                            <img src={f.url} alt={getDisplayName(f)} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 2 }} onError={e => e.target.style.display='none'} />
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{getDisplayName(f)}</span>
                            <button onClick={() => {
                              const imgs = [...(section.dubbing?.images || []), f.url]
                              const d = { ...section.dubbing, images: imgs }
                              onChange('dubbing', d)
                            }} style={{
                              fontSize: 10, color: '#fff', background: '#52c41a', border: 'none',
                              borderRadius: 3, cursor: 'pointer', padding: '2px 6px', flexShrink: 0
                            }}>+</button>
                            <span onClick={async () => {
                              if (!window.confirm('删除此文件？')) return
                              try {
                                const r = await fetch(API + '/courseware/files/' + encodeURIComponent(f.name), { method: 'DELETE' })
                                const rd = await r.json()
                                if (rd.success) { showMsg('[OK]'); loadFiles() }
                                else { showMsg('[ERR] ' + (rd.error || '删除失败'), 'error') }
                              } catch { showMsg('[ERR] 网络错误', 'error') }
                            }} style={{
                              fontSize: 12, color: '#ff4d4f', cursor: 'pointer', marginLeft: 4,
                              padding: '2px 4px', flexShrink: 0, border: '1px solid #ff4d4f',
                              borderRadius: 3,
                            }} title="删除文件">[DEL]</span>
                            <span onClick={async () => {
                              if (!window.confirm('删除此文件？')) return
                              try {
                                const res = await fetch(API + '/courseware/files/' + encodeURIComponent(f.name), { method: 'DELETE' })
                                const d2 = await res.json()
                                if (d2.success) {
                                  showMsg('✅ 已删除')
                                  loadFiles()
                                } else {
                                  showMsg('❌ ' + (d2.error || '删除失败'), 'error')
                                }
                              } catch {
                                showMsg('❌ 删除失败，网络错误', 'error')
                              }
                            }} style={{
                              fontSize: 12, color: '#ff4d4f', cursor: 'pointer', marginLeft: 2,
                              padding: '0 4px', flexShrink: 0, opacity: 0.5,
                            }} title="删除文件">🗑</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>

                {/* 台词文本 */}
                <div style={{ marginBottom: 6 }}>
                  <label style={{ fontSize: 10, color: '#888', display: 'block', marginBottom: 2 }}>台词文本（按"角色说：台词"格式，每句一行，孩子端逐句配音）</label>
                  <textarea value={section.dubbing?.script || ''} onChange={e => {
                    const d = { ...section.dubbing }
                    d.script = e.target.value
                    onChange('dubbing', d)
                  }} rows={4}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 11, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', lineHeight: 1.5 }}
                    placeholder={'博小兔说：大家好！\n乌龟爷爷说：欢迎来到七彩谷！\n旁白说：故事开始了……'} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: '#888', display: 'block', marginBottom: 2 }}>配音音频URL（可选，整段示范音频）</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input value={section.dubbing?.audioUrl || ''} onChange={e => {
                      const d = { ...section.dubbing }
                      d.audioUrl = e.target.value
                      onChange('dubbing', d)
                    }} placeholder="输入或粘贴音频URL"
                      style={{ flex: 1, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 11, minWidth: 0 }} />
                    {section.dubbing?.audioUrl && (
                      <>
                        <span style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer' }} onClick={() => navigator.clipboard.writeText(section.dubbing.audioUrl)}>📋</span>
                        <a href={section.dubbing.audioUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#52c41a', textDecoration: 'none' }}>▶</a>
                      </>
                    )}
                  </div>
                  {/* 直接上传音频 */}
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '5px 12px', background: '#722ed1', color: '#fff',
                      borderRadius: 4, cursor: 'pointer', fontSize: 11, userSelect: 'none',
                    }}>
                      📤 上传配音音频
                      <input type="file" accept="audio/*" style={{ display: 'none' }}
                        onChange={async e => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          const fd = new FormData()
                          fd.append('file', file)
                          try {
                            const res = await fetch(API + '/courseware/upload', { method: 'POST', body: fd })
                            const d2 = await res.json()
                            if (d2.success) {
                              showMsg('✅ 音频上传成功')
                              const nd = { ...section.dubbing, audioUrl: d2.url }
                              onChange('dubbing', nd)
                              loadFiles()
                            } else {
                              showMsg('❌ 上传失败: ' + (d2.error || '未知错误'), 'error')
                            }
                          } catch {
                            showMsg('❌ 上传失败，网络错误', 'error')
                          }
                          e.target.value = ''
                        }} />
                    </label>
                    <span style={{ fontSize: 10, color: '#999' }}>支持MP3/WAV等格式</span>
                  </div>
                  {/* 快速选择已上传音频 */}
                  {files.filter(f => f.name.match(/\.(mp3|wav|ogg|m4a|aac)$/i)).length > 0 && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ fontSize: 10, color: '#722ed1', cursor: 'pointer' }}>从已上传音频选择</summary>
                      <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 2, border: '1px solid #f0f0f0', borderRadius: 4, padding: 4 }}>
                        {files.filter(f => f.name.match(/\.(mp3|wav|ogg|m4a|aac)$/i)).map(f => (
                          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', fontSize: 10, borderBottom: '1px solid #f5f5f5' }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }} title={f.name}>🎵 {f.name}</span>
                            <span style={{ color: '#999', flexShrink: 0, fontSize: 10 }}>{f.sizeMB}MB</span>
                            <button onClick={() => {
                              const nd = { ...section.dubbing, audioUrl: f.url }
                              onChange('dubbing', nd)
                            }} style={{
                              fontSize: 10, color: '#fff', background: '#722ed1', border: 'none',
                              borderRadius: 3, cursor: 'pointer', padding: '2px 8px', flexShrink: 0
                            }}>选择</button>
                            <span onClick={async () => {
                              if (!window.confirm('删除此文件？')) return
                              try {
                                const r = await fetch(API + '/courseware/files/' + encodeURIComponent(f.name), { method: 'DELETE' })
                                const rd = await r.json()
                                if (rd.success) { showMsg('[OK]'); loadFiles() }
                                else { showMsg('[ERR] ' + (rd.error || '删除失败'), 'error') }
                              } catch { showMsg('[ERR] 网络错误', 'error') }
                            }} style={{
                              fontSize: 12, color: '#ff4d4f', cursor: 'pointer', marginLeft: 4,
                              padding: '2px 4px', flexShrink: 0, border: '1px solid #ff4d4f',
                              borderRadius: 3,
                            }} title="删除文件">[DEL]</span>
                            <span onClick={async () => {
                              if (!window.confirm('删除此文件？')) return
                              try {
                                const res = await fetch(API + '/courseware/files/' + encodeURIComponent(f.name), { method: 'DELETE' })
                                const d2 = await res.json()
                                if (d2.success) {
                                  showMsg('✅ 已删除')
                                  loadFiles()
                                } else {
                                  showMsg('❌ ' + (d2.error || '删除失败'), 'error')
                                }
                              } catch {
                                showMsg('❌ 删除失败，网络错误', 'error')
                              }
                            }} style={{
                              fontSize: 12, color: '#ff4d4f', cursor: 'pointer', marginLeft: 2,
                              padding: '0 4px', flexShrink: 0, opacity: 0.5,
                            }} title="删除文件">🗑</span>
                          </div>
                        ))}
                      </div>
                    
                {/* 做创作设置 */}
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 11, color: '#fa8c16', cursor: 'pointer', fontWeight: 500 }}>🎨 做创作设置（自由创编配音）</summary>
                  <div style={{ marginTop: 6, padding: 8, background: '#fffbe6', borderRadius: 4, border: '1px solid #ffe58f' }}>
                    {/* 创编提示文本 */}
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: '#888', display: 'block', marginBottom: 2 }}>💡 提示文本（孩子看到后即兴编故事配音）</label>
                      <textarea value={section.dubbing?.creative?.hints || ''} onChange={e => {
                        const d = { ...section.dubbing }
                        const cr = d.creative || {}
                        cr.hints = e.target.value
                        d.creative = cr
                        onChange('dubbing', d)
                      }} rows={2}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 11, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', lineHeight: 1.5 }}
                        placeholder={'小兔子来到了森林里，她遇到了谁？\n她们一起去了哪里？发生了什么有趣的事？'} />
                    </div>
                    {/* 创编背景图 */}
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: '#888', display: 'block', marginBottom: 4 }}>🖼 创编背景图（孩子端展示，可多张）</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                        {(section.dubbing?.creative?.images || []).map((img, ii) => {
                          const imgUrl = typeof img === 'string' ? img : (img.url || img)
                          return (
                            <div key={ii} style={{ position: 'relative', width: 60, height: 60, borderRadius: 4, overflow: 'hidden', border: '1px solid #e8e8e8', display: 'inline-block', marginRight: 4, marginBottom: 4 }}>
                              <img src={imgUrl} alt={'创编图'+(ii+1)} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.style.display = 'none'; e.target.parentNode.style.display = 'none' }} />
                              <span onClick={() => {
                                const imgs = (section.dubbing?.creative?.images || []).filter((_, j) => j !== ii)
                                const d = { ...section.dubbing, creative: { ...section.dubbing?.creative, images: imgs } }
                                onChange('dubbing', d)
                              }} style={{
                                position: 'absolute', top: 2, right: 2, width: 16, height: 16,
                                borderRadius: 8, background: 'rgba(255,0,0,0.7)', color: '#fff',
                                fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>✕</span>
                            </div>
                          )
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input value={section.dubbing?.creative?.newImageUrl || ''} onChange={e => {
                          const d = { ...section.dubbing }
                          const cr = d.creative || {}
                          cr.newImageUrl = e.target.value
                          d.creative = cr
                          onChange('dubbing', d)
                        }} placeholder="输入图片URL" style={{ flex: 1, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 11, minWidth: 0 }} />
                        <button onClick={() => {
                          const url = (section.dubbing?.creative?.newImageUrl || '').trim()
                          if (!url) return
                          const cr = section.dubbing?.creative || { images: [] }
                          const imgs = [...(cr.images || []), url]
                          const d = { ...section.dubbing, creative: { ...cr, images: imgs, newImageUrl: '' } }
                          onChange('dubbing', d)
                        }} style={{
                          padding: '4px 10px', background: '#fa8c16', color: '#fff', border: 'none',
                          borderRadius: 4, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap'
                        }}>添加</button>
                        <button onClick={() => {
                          const input = document.createElement('input')
                          input.type = 'file'
                          input.accept = 'image/*'
                          input.onchange = async (e) => {
                            const file = e.target?.files?.[0]
                            if (!file) { showMsg('❌ 未选择文件', 'error'); return }
                            showMsg('⏳ 正在上传...')
                            const fd = new FormData()
                            fd.append('file', file)
                            try {
                              const res = await fetch(API + '/courseware/upload', { method: 'POST', body: fd })
                              const d2 = await res.json()
                              if (d2.success) {
                                showMsg('✅ 创编图片上传成功')
                                const cr = section.dubbing?.creative || { images: [] }
                                const imgs = [...(cr.images || []), d2.url]
                                const nd = { ...section.dubbing, creative: { ...cr, images: imgs } }
                                onChange('dubbing', nd)
                                loadFiles()
                              } else {
                                showMsg('❌ ' + (d2.error || '上传失败'), 'error')
                              }
                            } catch (err) {
                              showMsg('❌ 上传失败: ' + (err.message || '网络错误'), 'error')
                            }
                            input.remove()
                          }
                          input.click()
                        }} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '4px 10px', background: '#52c41a', color: '#fff',
                          border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, userSelect: 'none', whiteSpace: 'nowrap'
                        }}>📤 上传图片</button>
                      </div>
                      {/* 快速选择已上传图片 */}
                      {files.filter(f => f.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)).length > 0 && (
                        <details style={{ marginTop: 4 }}>
                          <summary style={{ fontSize: 10, color: '#fa8c16', cursor: 'pointer' }}>从已上传图片选择</summary>
                          <div style={{ maxHeight: 100, overflowY: 'auto', marginTop: 2, border: '1px solid #f0f0f0', borderRadius: 4, padding: 4 }}>
                            {files.filter(f => f.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)).map(f => (
                              <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', fontSize: 10, borderBottom: '1px solid #f5f5f5' }}>
                                <img src={f.url} alt={f.name} style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 2 }} onError={e => e.target.style.display='none'} />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9 }}>{f.name}</span>
                                <button onClick={() => {
                                  const cr = section.dubbing?.creative || { images: [] }
                                  const imgs = [...(cr.images || []), f.url]
                                  const nd = { ...section.dubbing, creative: { ...cr, images: imgs } }
                                  onChange('dubbing', nd)
                                }} style={{
                                  fontSize: 9, color: '#fff', background: '#fa8c16', border: 'none',
                                  borderRadius: 3, cursor: 'pointer', padding: '1px 5px', flexShrink: 0
                                }}>+</button>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </details>

</details>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
